// =============================================================================
// CSO AGENT - Chief Sales Officer IA
// =============================================================================
// Agent IA pour la direction commerciale - Commercial & Ventes
// =============================================================================

import { useState } from "react";
import { 
  Crown, 
  TrendingUp, 
  ShoppingCart, 
  Users, 
  FileText, 
  BarChart3,
  Target,
  MessageSquare
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import CxOChatPanel from "../direction/CxOChatPanel";

export default function CSOAgent() {
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-950 via-green-950/20 to-slate-900" data-testid="cso-agent">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-green-500/20">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
            <Crown className="w-8 h-8 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">CSO IA</h1>
            <p className="text-sm text-gray-400">Chief Sales Officer - Commercial & Ventes</p>
          </div>
          <Badge className="ml-auto bg-green-500/20 text-green-400 border border-green-500/30">
            Actif
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-shrink-0 px-6 pt-4">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-green-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="chat" className="data-[state=active]:bg-green-600">
              <MessageSquare className="w-4 h-4 mr-2" />
              Mode Conversationnel
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* KPI Cards */}
            <Card className="bg-slate-800/50 border-green-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">CA Mensuel</p>
                    <p className="text-2xl font-bold text-white">--</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-blue-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Devis en cours</p>
                    <p className="text-2xl font-bold text-white">--</p>
                  </div>
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-amber-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Taux Conversion</p>
                    <p className="text-2xl font-bold text-white">--%</p>
                  </div>
                  <Target className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-purple-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Clients actifs</p>
                    <p className="text-2xl font-bold text-white">--</p>
                  </div>
                  <Users className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Activités récentes */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-green-400" />
                  Activités récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Les données commerciales seront affichées ici une fois connectées aux APIs.
                </p>
              </CardContent>
            </Card>

            {/* Objectifs */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-400" />
                  Objectifs du mois
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Définissez vos objectifs commerciaux pour suivre votre progression.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat" className="flex-1 overflow-hidden">
          <CxOChatPanel 
            cxoType="cso"
            agentLabel="CSO IA - Commercial & Ventes"
            agentColor="green"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
